import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrg } from '@/contexts/OrgContext';
import { CurrencyCode, DEFAULT_CURRENCY, getCurrencySetting } from '@/lib/currency';
import { normalisePaypalMeHandle } from '@/lib/paypal';
import { normalisePaymentPolicy, type PaymentPolicy } from '@/lib/paymentPolicy';

export type PaymentMethodSettings = {
  cash_in_person: { enabled: boolean; available: boolean };
  stripe: { enabled: boolean; available: boolean; status?: string; payment_link?: string; checkout_enabled?: boolean };
  paypal: {
    enabled: boolean;
    available: boolean;
    status?: 'active' | 'not_set_up' | string;
    /** PayPal.me handle only (e.g. "maidmadness"), never a URL. */
    paypal_me?: string;
  };
};

export const DEFAULT_PAYMENT_METHODS: PaymentMethodSettings = {
  cash_in_person: { enabled: true, available: true },
  stripe: { enabled: false, available: false, status: 'coming_soon', payment_link: '', checkout_enabled: false },
  paypal: { enabled: false, available: false, status: 'not_set_up', paypal_me: '' },
};

type RawPaymentMethod = {
  enabled?: boolean;
  available?: boolean;
  status?: string;
  payment_link?: string;
  url?: string;
  link?: string;
  checkout_enabled?: boolean;
  paypal_me?: string;
};

type RawPaymentMethods = {
  cash_in_person?: RawPaymentMethod;
  cash?: RawPaymentMethod;
  stripe?: RawPaymentMethod;
  card?: RawPaymentMethod;
  paypal?: RawPaymentMethod;
};

export function normalisePaymentMethods(raw: RawPaymentMethods | null | undefined): PaymentMethodSettings {
  const handle = normalisePaypalMeHandle(raw?.paypal?.paypal_me);
  return {
    cash_in_person: {
      enabled: raw?.cash_in_person?.enabled ?? raw?.cash?.enabled ?? true,
      available: true,
    },
    stripe: {
      enabled: false,
      available: false,
      status: raw?.stripe?.status ?? raw?.card?.status ?? 'coming_soon',
      // The Stripe Payment Link the owner configures (roadmap 1D). Accept the
      // same keys the send-enquiry-accepted edge function reads.
      payment_link: raw?.stripe?.payment_link ?? raw?.stripe?.url ?? raw?.stripe?.link ?? '',
      // Owner's "take card payments online" switch; only effective server-side
      // when the connected Stripe account has charges enabled.
      checkout_enabled: raw?.stripe?.checkout_enabled ?? false,
    },
    paypal: {
      // `enabled` is the owner's switch; `available` additionally needs a valid handle.
      enabled: Boolean(raw?.paypal?.enabled),
      available: Boolean(raw?.paypal?.enabled) && Boolean(handle),
      status: raw?.paypal?.enabled && handle ? 'active' : 'not_set_up',
      paypal_me: handle ?? '',
    },
  };
}

export function getActiveCustomerPaymentMethods(methods: PaymentMethodSettings) {
  return Object.entries(methods).filter(([, method]) => method.enabled && method.available);
}

/**
 * Loads currency / payment settings for an org.
 *
 * - Omit the argument (or pass `undefined`) → use the active org from OrgContext.
 * - Pass an org id → scope to that org.
 * - Pass `null` → explicitly no org; the query stays disabled.
 *
 * This honours the optional-parameter contract: omitting the argument and
 * passing `undefined` behave identically (the previous `arguments.length`
 * check made them diverge).
 */
export function useOrgSettings(orgIdOverride?: string | null) {
  const { orgId: contextOrgId, loading: orgLoading } = useOrg();
  // While the site's business is still resolving, OrgContext holds the fallback
  // org; wait rather than load (and briefly show) its currency and payment settings.
  const orgId = orgIdOverride !== undefined ? orgIdOverride : orgLoading ? null : contextOrgId;

  return useQuery({
    queryKey: ['org-settings', orgId],
    enabled: Boolean(orgId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('company_info')
        .select('id, currency_code, currency_name, currency_symbol, payment_methods, payment_policy')
        .eq('organization_id', orgId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      const currency = data
        ? {
            code: (data.currency_code ?? DEFAULT_CURRENCY.code) as CurrencyCode,
            name: data.currency_name ?? getCurrencySetting(data.currency_code).name,
            symbol: data.currency_symbol ?? getCurrencySetting(data.currency_code).symbol,
          }
        : DEFAULT_CURRENCY;

      return {
        companyInfoId: data?.id ?? null,
        currency,
        paymentMethods: normalisePaymentMethods(data?.payment_methods),
        paymentPolicy: normalisePaymentPolicy(data?.payment_policy as Partial<PaymentPolicy> | null | undefined),
      };
    },
  });
}
